import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useAppDispatch } from '../../hooks';
import { useToken } from '../../hooks/useToken';
import { PageCenteredLayout } from '../layout/PageCenteredLayout';
import { ClockIcon } from '@heroicons/react/24/outline';
import EventProgressChart from '../event/EventProgressChart';
import EventAttendeeProgressTable from '../event/EventAttendeeProgressTable';
import { fetchEventDetails, fetchEventAttendees, type IEventMilestone } from '../../reducers/eventsSlice';
import { useEvent } from '../../hooks/useEvent';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import SectionLayout from '../layout/SectionLayout';
import EventAccountPoolAllocationBar from '../event/EventAccountPoolAllocationBar';
import EventHeader from '../event/EventHeader';
import NoMatchEvents from './NoMatchEvents';
import { dateFormat, dateTimeFormat } from '../../utilities/constants';

const calculateTimeRemaining = (startDateTime: string, endDateTime: string): string => {
  const now = dayjs();
  const start = dayjs(startDateTime);
  const end = dayjs(endDateTime);

  if (now.isBefore(start)) {
    const daysUntilStart = start.diff(now, 'day');
    if (daysUntilStart > 0) {
      return `Event starts in ${daysUntilStart} day${daysUntilStart > 1 ? 's' : ''}`;
    }
    const diff = start.diff(now, 'second');
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return `Starts in ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  if (now.isAfter(end)) {
    return 'Event ended';
  }

  const diff = end.diff(now, 'second');
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;
};

const findNextExpectedMilestone = (milestones: IEventMilestone[]) => {
  const now = dayjs();
  const nextMilestone = milestones
    .filter((m) => dayjs(m.expected_by_timestamp).isAfter(now))
    .sort((a, b) => dayjs(a.expected_by_timestamp).diff(dayjs(b.expected_by_timestamp)))[0];

  if (!nextMilestone) return null;

  const minutesRemaining = dayjs(nextMilestone.expected_by_timestamp).diff(now, 'minute');
  return { milestone: nextMilestone, minutesRemaining };
};

export default function EventInsights() {
  const token = useToken();
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const { eventSlug } = useParams();
  const { event } = useEvent(eventSlug);
  const dispatch = useAppDispatch();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [sortedMilestones, setSortedMilestones] = useState<IEventMilestone[]>([]);
  const [nextMilestone, setNextMilestone] = useState<{ milestone: IEventMilestone; minutesRemaining: number } | null>(
    null,
  );

  // Add data refresh effect
  useEffect(() => {
    if (eventSlug) {
      const fetchData = () => {
        dispatch(fetchEventDetails({ token, role, slug: eventSlug }))
          .then(() => {
            dispatch(fetchEventAttendees({ token, role, slug: eventSlug })).catch((err) => {
              console.log('Fetch eventAttendees error:', err);
            });
          })
          .catch((err) => {
            console.log('Fetch event error:', err);
          });
      };

      // Initial fetch
      fetchData();

      // Set up periodic refresh
      const refreshInterval = setInterval(fetchData, 60000); // Refresh every minute

      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, [eventSlug, role, token]);

  useEffect(() => {
    if (event?.start_datetime && event?.end_datetime) {
      setTimeRemaining(calculateTimeRemaining(event.start_datetime, event.end_datetime));

      const timer = setInterval(() => {
        setTimeRemaining(calculateTimeRemaining(event.start_datetime, event.end_datetime));
      }, 1000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [event?.start_datetime, event?.end_datetime]);

  useEffect(() => {
    if (event?.milestones) {
      setSortedMilestones(event.milestones.slice().sort((a, b) => a.sequence_order - b.sequence_order));
    }
  }, [event?.milestones]);

  useEffect(() => {
    if (sortedMilestones.length > 0) {
      const updateNextMilestone = () => {
        setNextMilestone(findNextExpectedMilestone(sortedMilestones));
      };

      updateNextMilestone();
      const timer = setInterval(updateNextMilestone, 60000); // Update every minute

      return () => {
        clearInterval(timer);
      };
    }
  }, [sortedMilestones]);

  if (!event) {
    return <NoMatchEvents />;
  }

  const formattedStartDate = dayjs(event.start_datetime).format(dateFormat);
  const totalReadyAccounts = event.account_pool?.total_ready_child_accounts ?? 0;
  const allocatedAccounts = event.account_pool?.total_allocated_child_accounts ?? 0;
  const allocatedPercentage = totalReadyAccounts === 0 ? 0 : (allocatedAccounts / totalReadyAccounts) * 100;

  return (
    <>
      <main>
        <EventHeader
          eventName={event.name}
          eventSlug={event.slug}
          eventLocation={event.location}
          formattedStartDate={formattedStartDate}
          activeTab="insights"
          titleSuffix={<> / Insights</>}
          headerActions={
            <div className="flex flex-col items-end gap-y-2 sm:gap-x-1">
              {/* Event time remaining - only show during or after event */}
              {!dayjs().isBefore(dayjs(event.start_datetime)) && (
                <div
                  className="flex items-center justify-left text-nowrap font-bold font-mono text-4xl"
                  style={{ color: '#29B5E8' }}
                >
                  <ClockIcon className="mr-2 h-[1em]" style={{ color: '#29B5E8' }} />
                  <span>{timeRemaining}</span>
                </div>
              )}
              {/* Next expected milestone to be completed and when */}
              <div className="text-gray-500">
                {nextMilestone
                  ? `"${nextMilestone.milestone.title}" due in ${nextMilestone.minutesRemaining} minutes`
                  : 'No upcoming milestones'}
              </div>
              {/* Only show start/end times before event starts */}
              {dayjs().isBefore(dayjs(event.start_datetime)) && (
                <div className="flex flex-col gap-y-1 text-sm text-gray-500 text-right">
                  <p>Start: {dayjs(event.start_datetime).format(dateTimeFormat)}</p>
                  <p>End: {dayjs(event.end_datetime).format(dateTimeFormat)}</p>
                </div>
              )}
            </div>
          }
        />

        <PageCenteredLayout extraClassName="py-8">
          <div className="w-full grid grid-cols-2 items-start gap-x-24 text-dataops-yale-blue">
            <EventAccountPoolAllocationBar allocatedPercentage={allocatedPercentage} label="Account Pool" />
            <div className="flex-1"></div> {/* Empty div to maintain the grid layout */}
          </div>
        </PageCenteredLayout>

        {event.eventAttendees ? (
          <>
            <PageCenteredLayout extraClassName="py-8">
              <EventProgressChart eventMilestones={sortedMilestones} eventAttendees={event.eventAttendees} />
            </PageCenteredLayout>

            <PageCenteredLayout extraClassName="py-8">
              <EventAttendeeProgressTable
                eventMilestones={sortedMilestones}
                eventAttendees={event.eventAttendees}
                nextMilestoneId={nextMilestone?.milestone.id ?? null}
              />
            </PageCenteredLayout>
          </>
        ) : (
          <InsightsPageSkeleton />
        )}
      </main>
    </>
  );
}

function InsightsPageSkeleton() {
  return (
    <>
      <PageCenteredLayout extraClassName="py-8">
        {/* <div className="h-64 bg-gray-400" /> */}
        <SectionLayout
          skeleton
          label="EventProgressChart"
          className="bg-dataops-platinum-gray/40 md:col-span-1 md:col-start-3 h-64"
        >
          <div className="flex space-x-2">
            <p className="w-full h-36 rounded-lg bg-shine flex-col"></p>
            <p className="w-full h-36 rounded-lg bg-shine flex-col"></p>
            <p className="w-full h-36 rounded-lg bg-shine flex-col"></p>
          </div>
        </SectionLayout>
      </PageCenteredLayout>
      <PageCenteredLayout extraClassName="py-8">
        <SectionLayout
          skeleton
          label="EventAttendeeProgressTable"
          className="bg-dataops-platinum-gray/40 md:col-span-1 md:col-start-3 h-64"
        >
          <div className="space-y-2">
            <p className="w-full h-12 rounded-lg bg-shine"></p>
            <p className="w-full h-12 rounded-lg bg-shine"></p>
            <p className="w-full h-12 rounded-lg bg-shine"></p>
          </div>
        </SectionLayout>
      </PageCenteredLayout>
      ;
    </>
  );
}
